<script setup>
import {computed, useTemplateRef} from "vue";
import CountsBlock from "@/components/hpo/dashboard/CountsBlock";

import AllPersonsList from "./patientListItems/AllPersonsList.vue";
import {mdiDownload} from "@mdi/js";
import {useQuery} from "@vue/apollo-composable";
import {useLastResult} from "@/graphql/composable";
import {IzsPatientListQuery} from "@/graphql/queries/hpo/patients.graphql";
import {useSearch} from "@/search";
import {HpoRole} from "../choices";
import AddInjuredAndUninjuredButtons from "./AddInjuredAndUninjuredButtons.vue";
import {isIzsWorker} from "../utils";
import DashboardModalsContainer from "@/components/hpo/dashboard/DashboardModalsContainer.vue";
import QrScanButton from "@/components/hpo/QrScanButton.vue";

const props = defineProps({
  hpo: {
    type: Object,
    required: true
  },
  member: {
    type: Object,
    required: true
  },
  patientCounts: {
    type: Object,
    required: true
  },
  patientCountsLoading: {
    type: Boolean,
    required: true
  },
  hpoPassword: {
    type: String,
    required: true
  },
  countries: {
    type: Array,
    required: true
  },
  mobileView: {
    type: Boolean,
    required: true,
  },
  chipProperties: {
    type: Object,
    required: true
  },
  personsTrackerTimestamp: {
    type: Object,
    required: true
  },
})

const modals = useTemplateRef('modals');

// Persons Query
const personsQueryVariables = computed(() => ({
  updateTimestamp: props.personsTrackerTimestamp,
  hpoId: props.hpo.id
}));
const {result: patientListResult, loading: patientListLoading} = useQuery(IzsPatientListQuery, personsQueryVariables)
const patientList = useLastResult(patientListResult, [], data => data.izsPatientList);

const {query, searchResultsOrAllItems: filteredPatientList} = useSearch(
  patientList, ['firstName', 'lastName', 'birthNumber', 'birthYear', 'note'], ['tic']
);

const anyLoading = computed(() => {
  return props.patientCountsLoading || patientListLoading.value
})

function displayTriageWizard(patient) {
  modals.value.showTriageWizard(patient);
}

function displayIdentificationWizard(patient) {
  modals.value.showIdentificationWizard(patient);
}

function displayActionsModal(patient) {
  modals.value.showActionsModal(patient);
}

function displayPatientModal(patient) {
  modals.value.showPatientModal(patient);
}
</script>

<template>
  <v-container fluid class="pa-0">
    <v-row dense v-if="patientCounts && !isIzsWorker(member.role)" class="mb-6">
      <v-col cols="12" class="d-flex justify-center ga-3 flex-wrap">
        <CountsBlock :hpoId="hpo.id"
                     :title="pgettext('label', 'Estimated total')"
                     :member="member"
                     :loading="patientCountsLoading"
                     :patientCounts="patientCounts.sum"
                     :showNationalities="false"
                     :legend="pgettext('label', 'Estimated number of all persons')"/>
        <CountsBlock :hpoId="hpo.id"
                     :title="pgettext('label', 'Estimated on scene')"
                     :member="member"
                     :loading="patientCountsLoading"
                     :patientCounts="patientCounts.onScene"
                     :showNationalities="false"
                     :legend="pgettext('label', 'Estimated number of persons on scene')"/>
        <CountsBlock :hpoId="hpo.id"
                     :title="pgettext('label', 'Registered total')"
                     :member="member"
                     :loading="patientCountsLoading"
                     :patientCounts="patientCounts.registered"
                     :legend="pgettext('label', 'Total number of persons registered in system')"/>
        <CountsBlock :hpoId="hpo.id"
                     :title="pgettext('label', 'Registered on scene')"
                     :member="member"
                     :loading="patientCountsLoading"
                     :patientCounts="patientCounts.registeredOnScene"
                     :legend="pgettext('label', 'Registered persons in system currently on scene')"/>
        <CountsBlock :hpoId="hpo.id"
                     :title="pgettext('label', 'Being transported')"
                     :member="member"
                     :loading="patientCountsLoading"
                     :patientCounts="patientCounts.beingTransported"
                     :hideBlackAndWhite="true"
                     :legend="pgettext('label', 'Number of patients being transported')"/>
        <CountsBlock :hpoId="hpo.id"
                     :title="pgettext('label', 'In hospital')"
                     :member="member"
                     :loading="patientCountsLoading"
                     :patientCounts="patientCounts.inHospital"
                     :hideBlackAndWhite="true"
                     :legend="pgettext('label', 'Number of patients in hospital')"/>
        <CountsBlock :hpoId="hpo.id"
                     :title="pgettext('label', 'Identified by name')"
                     :member="member"
                     :loading="patientCountsLoading"
                     :patientCounts="patientCounts.withName"
                     :legend="pgettext('label', 'Number of persons identified by name')"/>
        <CountsBlock :hpoId="hpo.id"
                     :title="pgettext('label', 'Verified by Police')"
                     :member="member"
                     :loading="patientCountsLoading"
                     :patientCounts="patientCounts.verified"
                     :legend="pgettext('label', 'Number of patients identified by the police')"/>

      </v-col>
    </v-row>
    <template v-if="member.role !== HpoRole.VIEWER">
      <v-row dense v-if="isIzsWorker(member.role)">
        <AddInjuredAndUninjuredButtons @displayTriageWizard="displayTriageWizard"
                                       @displayIdentificationWizard="displayIdentificationWizard"/>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-btn v-if="!isIzsWorker(member.role)"
                 :href="reverseUrl('web:hpo:download_patients', hpo.id)" :prependIcon="mdiDownload">
            {{ pgettext('action', 'Export patients') }}
          </v-btn>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="8">
          <v-text-field clearable
                        variant="outlined"
                        :hide-spin-buttons="true"
                        :loading="patientListLoading"
                        :label="pgettext('title', 'Patient search')"
                        :placeholder="pgettext('label', 'Put in TIC Card number / name / birth year / birth number / note')"
                        hide-details="auto"
                        v-model="query"/>
        </v-col>
        <v-col cols="12" md="4">
          <QrScanButton @scan="query = $event.tic"/>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <AllPersonsList :patientList="filteredPatientList"
                          :hpo="hpo"
                          :member="member"
                          :anyLoading="anyLoading"
                          :alertList="alertList"
                          :now="now"
                          :countries="countries"
                          @displayActionsModal="displayActionsModal"/>
        </v-col>
      </v-row>
    </template>
  </v-container>

  <DashboardModalsContainer ref="modals"
                            :countries="countries"
                            :member="member"
                            :hpo="hpo"
                            :patientList="patientList"/>
</template>
